import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { initializeFirebaseMessaging } from '@/shared/config/firebase.config';
import { FcmDataModel } from '@/shared/models/fcm-data.model';
import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import * as fcmData from '@/shared/services/data/user-data/fcm.data';

export interface NotificationPayload {
  title?: string;
  body?: string;
  icon?: string;
  data?: { [key: string]: any };
}

// Global state for singleton pattern
let isMessagingInitialized = false;
let cachedFCMToken: string | null = null;
let tokenRegistrationPromise: Promise<boolean> | null = null;
let messagingUnsubscribeFunction: (() => void) | null = null;
const processedMessages = new Set<string>();
const MESSAGE_EXPIRY = 5000; // 5 seconds

/**
 * Check if notifications are supported in this browser
 */
const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Request notification permission from the user
 * This should only be called in response to a user gesture
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    if (!isNotificationSupported()) {
      console.log('Notifications are not supported in this browser');
      return false;
    }

    // Only request if permission is not already determined
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Check if notification permission is granted without requesting
 */
export const hasNotificationPermission = (): boolean => {
  return Notification.permission === 'granted';
};

/**
 * Request permission and initialize messaging - call this from user gesture handlers
 */
export const requestPermissionAndInitialize = async (
  onMessageCallback?: (payload: MessagePayload) => void,
  useCustomBanner: boolean = true
): Promise<(() => void) | null> => {
  try {
    console.log('[requestPermissionAndInitialize] Starting permission request and initialization');

    if (!isNotificationSupported()) {
      console.log(
        '[requestPermissionAndInitialize] Notifications are not supported in this browser'
      );
      return null;
    }

    console.log('[requestPermissionAndInitialize] Requesting notification permission...');
    // Request permission (must be called from user gesture)
    const hasPermission = await requestNotificationPermission();

    console.log(
      '[requestPermissionAndInitialize] Permission result:',
      hasPermission,
      'Current permission:',
      Notification.permission
    );

    if (!hasPermission) {
      console.log('[requestPermissionAndInitialize] Notification permission denied by user');
      return null;
    }

    console.log('[requestPermissionAndInitialize] Permission granted, initializing messaging...');
    // Now initialize messaging
    const result = await initializeMessaging(onMessageCallback, useCustomBanner);
    console.log(
      '[requestPermissionAndInitialize] Messaging initialization result:',
      result ? 'Success' : 'Failed'
    );
    return result;
  } catch (error) {
    console.error(
      '[requestPermissionAndInitialize] Error requesting permission and initializing:',
      error
    );
    if (error instanceof Error) {
      console.error('[requestPermissionAndInitialize] Error name:', error.name);
      console.error('[requestPermissionAndInitialize] Error message:', error.message);
    }
    return null;
  }
};

/**
 * Get the FCM registration token with caching
 */
export const getFCMToken = async (): Promise<string | null> => {
  try {
    console.log('[getFCMToken] Starting token retrieval process');

    if (!isNotificationSupported()) {
      console.log('[getFCMToken] Notifications are not supported in this browser');
      return null;
    }

    // Return cached token if available
    if (cachedFCMToken) {
      console.log('[getFCMToken] Using cached FCM token:', cachedFCMToken);
      return cachedFCMToken;
    }

    console.log('[getFCMToken] Initializing Firebase messaging...');
    const messaging = await initializeFirebaseMessaging();
    if (!messaging) {
      console.log('[getFCMToken] Firebase messaging not supported');
      return null;
    }

    // Check if we have permission - DO NOT request automatically
    if (Notification.permission !== 'granted') {
      console.log(
        '[getFCMToken] Notification permission not granted. Current permission:',
        Notification.permission,
        '. Call requestPermissionAndInitialize() from a user gesture.'
      );
      return null;
    }

    console.log('[getFCMToken] Permission granted, proceeding with token generation');

    // Get PUBLIC_URL from environment
    const publicUrl = import.meta.env.BASE_URL || '';
    console.log('Using PUBLIC_URL:', publicUrl);

    // Clean up public URL - ensure it starts with / and has no trailing /
    const cleanPublicUrl = ('/' + publicUrl.replace(/^\/+/, '').replace(/\/+$/, '')).replace(
      /\/+/g,
      '/'
    );
    console.log('Cleaned PUBLIC_URL:', cleanPublicUrl);

    // Construct service worker paths
    const serviceWorkerPath = `${cleanPublicUrl}/firebase-messaging-sw.js`;
    const serviceWorkerScope = cleanPublicUrl;

    console.log('Registering service worker with:', {
      path: serviceWorkerPath,
      scope: serviceWorkerScope
    });

    // Get existing service worker registration to prevent duplicates
    let serviceWorkerRegistration;
    if ('serviceWorker' in navigator) {
      try {
        // First try to get any existing registration
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Existing service worker registrations:', registrations);

        // Look for our specific service worker
        serviceWorkerRegistration = registrations.find((reg) =>
          reg.scope.includes(serviceWorkerScope)
        );

        if (!serviceWorkerRegistration) {
          // Register service worker if not exists
          serviceWorkerRegistration = await navigator.serviceWorker.register(serviceWorkerPath, {
            scope: serviceWorkerScope
          });
          console.log('Service Worker registered:', serviceWorkerRegistration);
        } else {
          console.log('Using existing Service Worker registration:', serviceWorkerRegistration);

          // Force update the existing service worker
          const registration = await serviceWorkerRegistration.update();
          console.log('Service Worker updated:', registration);
        }

        // Prepare environment data
        const envData = {
          PUSH_NOTIFICATION_SCHEME: import.meta.env.VITE_PUSH_NOTIFICATION_SCHEME,
          PUSH_NOTIFICATION_SCHEME_FALLBACK: import.meta.env.VITE_PUSH_NOTIFICATION_SCHEME_FALLBACK,
          PUBLIC_URL: cleanPublicUrl,
          FIREBASE_CONFIG: {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: import.meta.env.VITE_FIREBASE_APP_ID,
            measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
          }
        };

        // Function to send environment data
        const sendEnvToServiceWorker = async (registration: ServiceWorkerRegistration) => {
          if (registration.active) {
            registration.active.postMessage({
              type: 'SET_ENV_VARS',
              data: envData
            });
            console.log('Environment data sent to service worker');
          }
        };

        // Send environment data
        await sendEnvToServiceWorker(serviceWorkerRegistration);

        // Also listen for controller changes
        navigator.serviceWorker.addEventListener('controllerchange', async () => {
          const newRegistration = await navigator.serviceWorker.ready;
          await sendEnvToServiceWorker(newRegistration);
        });
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        console.error('Attempted path:', serviceWorkerPath);
        console.error('Attempted scope:', serviceWorkerScope);
        throw error;
      }
    }

    // Get VAPID key
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.error(
        '[getFCMToken] VAPID key is missing! Please add VITE_FIREBASE_VAPID_KEY to your .env.local file'
      );
      return null;
    }

    console.log(
      '[getFCMToken] VAPID key found, generating token with service worker:',
      serviceWorkerRegistration?.scope
    );

    // Get the token
    const token = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: serviceWorkerRegistration
    });

    if (token) {
      console.log('[getFCMToken] ✅ FCM Registration token generated successfully:', token);
      cachedFCMToken = token; // Cache the token
      return token;
    } else {
      console.log('[getFCMToken] ⚠️ No registration token available.');
      return null;
    }
  } catch (error) {
    console.error('[getFCMToken] ❌ Error occurred while retrieving token:', error);
    if (error instanceof Error) {
      console.error('[getFCMToken] Error name:', error.name);
      console.error('[getFCMToken] Error message:', error.message);
      console.error('[getFCMToken] Error stack:', error.stack);
    }
    return null;
  }
};

/**
 * Register FCM token with server (with deduplication)
 */
const registerTokenWithServer = async (token: string): Promise<boolean> => {
  // Check if this token is already being registered
  if (tokenRegistrationPromise) {
    console.log('Token registration already in progress, waiting for completion...');
    return await tokenRegistrationPromise;
  }

  // Check if token was already registered in this session
  const lastRegisteredToken = localStorage.getItem('fcm_token_registered');
  if (lastRegisteredToken === token) {
    console.log('Token already registered in this session, skipping registration');
    return true;
  }

  // Create registration promise
  tokenRegistrationPromise = (async () => {
    try {
      console.log('Registering FCM token with server:', token);

      const fcmTokenData: FcmDataModel = {
        token,
        platform: 'internal_admin',
        client: 'web'
      };

      const result = await fcmData.updateClientFcmToken(fcmTokenData, 'notification');

      if (result === true) {
        console.log('FCM token registered successfully');
        localStorage.setItem('fcm_token_registered', token);
        return true;
      } else if (isErrorInfo(result)) {
        console.error('Failed to register FCM token:', result);
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error saving FCM token to server:', error);
      return false;
    } finally {
      // Clear the promise so future calls can proceed
      tokenRegistrationPromise = null;
    }
  })();

  return await tokenRegistrationPromise;
};

/**
 * Initialize Firebase Cloud Messaging with singleton pattern
 * This version does NOT request permission automatically
 */
export const initializeMessaging = async (
  onMessageCallback?: (payload: MessagePayload) => void,
  useCustomBanner: boolean = true
): Promise<(() => void) | null> => {
  try {
    // Return existing unsubscribe function if already initialized
    if (isMessagingInitialized && messagingUnsubscribeFunction) {
      console.log('Firebase messaging already initialized, using existing instance');
      return messagingUnsubscribeFunction;
    }

    const messaging = await initializeFirebaseMessaging();
    if (!messaging) {
      console.log('Firebase messaging not supported');
      return null;
    }

    // Only proceed if we already have permission
    if (Notification.permission === 'granted') {
      const token = await getFCMToken();

      if (token) {
        await registerTokenWithServer(token);
      }
    } else {
      console.log(
        'Notification permission not granted. Use requestPermissionAndInitialize() from a user gesture.'
      );
    }

    // Set up message handler only if not already set up
    if (!isMessagingInitialized) {
      // Handle foreground messages
      const unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        console.log('Message received in foreground:', payload);

        // Check for duplicate messages
        const messageId = payload.messageId || payload.data?.messageId || Date.now().toString();
        if (processedMessages.has(messageId)) {
          console.log('Duplicate foreground message detected, skipping', messageId);
          return;
        }

        // Add to processed messages and set cleanup timeout
        processedMessages.add(messageId);
        setTimeout(() => {
          processedMessages.delete(messageId);
        }, MESSAGE_EXPIRY);

        // Only handle if app is visible
        if (document.visibilityState === 'visible') {
          if (useCustomBanner && onMessageCallback) {
            // Use custom banner
            onMessageCallback(payload);
          } else {
            // Use browser notification
            displayBrowserNotification({
              title: payload.notification?.title,
              body: payload.notification?.body,
              icon: payload.notification?.icon,
              data: payload.data
            });
          }
        }
      });

      messagingUnsubscribeFunction = unsubscribe;
      isMessagingInitialized = true;
      console.log('Firebase messaging initialized successfully');
    }

    return messagingUnsubscribeFunction;
  } catch (error) {
    console.error('Error initializing messaging:', error);
    return null;
  }
};

/**
 * Reset messaging state (useful for testing or logout)
 */
export const resetMessagingState = (): void => {
  isMessagingInitialized = false;
  cachedFCMToken = null;
  tokenRegistrationPromise = null;

  if (messagingUnsubscribeFunction) {
    messagingUnsubscribeFunction();
    messagingUnsubscribeFunction = null;
  }

  localStorage.removeItem('fcm_token_registered');
  console.log('Firebase messaging state reset');
};

/**
 * Display a browser notification (fallback for when custom banner is not used)
 */
const displayBrowserNotification = (payload: NotificationPayload) => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return;
  }

  if (Notification.permission === 'granted') {
    const notification = new Notification(payload.title || 'New Message', {
      body: payload.body,
      icon: payload.icon || '/favicon.ico',
      data: payload.data,
      requireInteraction: false,
      silent: false
    });

    notification.onclick = function (event) {
      event.preventDefault();

      // Handle notification click
      if (payload.data?.url) {
        window.open(payload.data.url, '_blank');
      } else {
        window.focus();
      }

      notification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
  }
};
